import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useCombobox } from 'downshift';
import { invert } from 'lodash';
import { createBemBlockBuilder, SearchAutocomplete } from '@ebsco-ui/ebsco-ui';

import { AutoCompleteGroups, SEARCH_OPTION } from '@app/components';
import { useAutoComplete } from '@app/hooks';
import { sharedMessages } from '@app/translations';

import {
  ADVANCED_SEARCH_FIELD,
  ADVANCED_SEARCH_OPTIONS,
  ADVANCED_SEARCH_QUALIFIER,
  BOOLEAN_CHIP,
  FIELDS_TO_SEARCH_OPTION_MAP,
  RowValues,
  SEARCH_OPTION_TO_FIELD_MAP,
} from '../constants';
import { FieldsDropdown } from '../FieldsDropdown';

import './SearchRow.scss';

interface SearchRowProps extends RowValues {
  index: number;
  totalRowsCount: number;
  updateRow: (data: RowValues) => void;
}

const cnBem = createBemBlockBuilder(['advancedSearchRow']);

export const ADVANCED_OPTION_TO_SEARCH_OPTION_MAP = {
  [ADVANCED_SEARCH_OPTIONS.keyword]: SEARCH_OPTION.keyword,
  [ADVANCED_SEARCH_OPTIONS.title]: SEARCH_OPTION.title,
  [ADVANCED_SEARCH_OPTIONS.subjects]: SEARCH_OPTION.subject,
  [ADVANCED_SEARCH_OPTIONS.contributors]: SEARCH_OPTION.author,
};

export const mapSearchOptionToField = (option: SEARCH_OPTION) =>
  SEARCH_OPTION_TO_FIELD_MAP[
    invert(ADVANCED_OPTION_TO_SEARCH_OPTION_MAP)[option]
  ] as ADVANCED_SEARCH_FIELD;

export const SearchRow: FC<SearchRowProps> = ({
  value,
  field,
  qualifier,
  index,
  totalRowsCount,
  booleanChip,
  updateRow,
}) => {
  const { $t } = useIntl();
  const searchOption =
    ADVANCED_OPTION_TO_SEARCH_OPTION_MAP[FIELDS_TO_SEARCH_OPTION_MAP[field]];

  const onSelectItem = ({ selectedValue, selectedOption }) => {
    updateRow({
      value: selectedValue,
      field: mapSearchOptionToField(selectedOption),
      booleanChip,
      qualifier,
    });
  };

  const { inputValue, suggestions, handleSelectItem, handleInputValueChange } =
    useAutoComplete({ value, searchOption, onSelectItem });

  const isKeywordSearch = searchOption === SEARCH_OPTION.keyword;

  const handleInputChange = changes => {
    handleInputValueChange(changes);

    if (
      changes.type === useCombobox.stateChangeTypes.InputChange ||
      changes.type === useCombobox.stateChangeTypes.FunctionReset
    ) {
      updateRow({
        value: changes.inputValue,
        booleanChip,
        field,
        qualifier,
      });
    }
  };

  const handleBooleanChipChange = (updatedChip: BOOLEAN_CHIP) => {
    updateRow({ value, field, qualifier, booleanChip: updatedChip });
  };

  const handleFieldChange = (updatedField: ADVANCED_SEARCH_FIELD) => {
    updateRow({
      value,
      booleanChip,
      qualifier,
      field: updatedField,
    });
  };

  const handleQualifierChange = (
    updatedQualifier: ADVANCED_SEARCH_QUALIFIER
  ) => {
    updateRow({ value, booleanChip, field, qualifier: updatedQualifier });
  };

  const autocompleteLabel = $t({
    id: 'advancedSearch.inputField',
    defaultMessage: 'Search input field',
  });
  const legendId = `row-${index}-legend`;

  return (
    <fieldset
      role="group"
      aria-label={$t({
        id: 'advancedSearch.row',
        defaultMessage: 'Row',
      })}
      aria-describedby={legendId}
      className={cnBem()}
    >
      <legend id={legendId} className={cnBem('__legend')}>
        {$t(
          {
            id: 'advancedSearch.rowTitle',
            defaultMessage: 'Row {number} of {total}.',
          },
          {
            number: index + 1,
            total: totalRowsCount,
          }
        )}
      </legend>
      <div className={cnBem('__wrapper')}>
        <div className={cnBem('__autocompleteGroup')}>
          {booleanChip && (
            <FieldsDropdown
              option={booleanChip}
              type="chip"
              id={`row-${index}-chip`}
              className={cnBem('__chipDropdown')}
              onSelect={handleBooleanChipChange}
            />
          )}
          <SearchAutocomplete
            items={suggestions}
            {...(isKeywordSearch && {
              groups: AutoCompleteGroups,
              groupItemsBy: item => item.category,
            })}
            value={inputValue}
            messages={{
              autocompleteMessages: {
                input: {
                  'aria-label': autocompleteLabel,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  'aria-describedby': legendId,
                },
                clearButton: {
                  'aria-label': $t(sharedMessages.clear),
                },
                menu: {
                  'aria-label': autocompleteLabel,
                },
              },
            }}
            displayHighlightedValue
            focusedByDefault={index === 0}
            className={cnBem('__autocomplete')}
            downshiftOptions={{
              menuId: `row-${index}-autocomplete-menu`,
              onSelectedItemChange: handleSelectItem,
            }}
            onChange={handleInputChange}
          />
        </div>
        <FieldsDropdown
          option={qualifier}
          type="qualifier"
          id={`row-${index}-qualifier`}
          className={cnBem('__dropdown')}
          onSelect={handleQualifierChange}
        />
        <FieldsDropdown
          option={field}
          type="field"
          id={`row-${index}-field`}
          className={cnBem('__dropdown')}
          onSelect={handleFieldChange}
        />
      </div>
    </fieldset>
  );
};
